import { type NextRouter, useRouter } from 'next/router'
import { useMemo } from 'react'

import { appendLeadId } from './leadId'
import { useLeadId } from './useLeadId'

/* push and replace share one signature — (url, as?, options?) — so one tuple type covers both. */
type NavigateArgs = Parameters<NextRouter['push']>

/**
 * Drop-in replacement for `next/router`'s `useRouter` that re-appends the
 * current `lead_id` to any `push` / `replace` navigation. Every other router
 * property/method passes through unchanged, so call sites only swap the hook
 * name and keep `router.push(...)` as-is. No-op when no `lead_id` is present.
 *
 * `next/navigation`'s `useRouter` is banned (see docs/agents/attribution.md): with no `app/`
 * directory it is a reduced shim over this same router. See WEB-903.
 */
export function useTrackedRouter(): NextRouter {
  const router = useRouter()
  const leadId = useLeadId()

  return useMemo(() => {
    /* Track both positional URLs, and forward only the arguments actually passed.

       `as` is the URL the browser address bar shows, so leaving it untracked drops the id from the
       visible URL even when `url` carries it — and the next reload requests `as`, arrives without a
       lead_id, and the middleware mints a replacement. That is the exact failure this hook exists to
       prevent. No call site passes `as` today; this keeps the primitive correct for the one that will.

       Arity is preserved deliberately: spreading a fixed (url, as, options) signature would turn
       `push(url)` into `push(url, undefined, undefined)` — identical behaviour, but observably
       different to a spy, and a drop-in must not change the call shape. */
    const trackArgs = (args: NavigateArgs): NavigateArgs => {
      const [url, as, ...options] = args
      if (args.length === 1) return [appendLeadId(url, leadId)]
      return [appendLeadId(url, leadId), as === undefined ? as : appendLeadId(as, leadId), ...options]
    }

    const push: NextRouter['push'] = (...args) => router.push(...trackArgs(args))
    const replace: NextRouter['replace'] = (...args) => router.replace(...trackArgs(args))

    // Cache bound methods so reference equality holds across accesses —
    // otherwise `router.back !== router.back`, and any effect/memo that lists a
    // router method in its deps re-runs on every render.
    const boundMethods = new Map<PropertyKey, unknown>()

    return new Proxy(router, {
      get(target, prop) {
        if (prop === 'push') return push
        if (prop === 'replace') return replace
        // Read with `this` = the real router (not the Proxy) so accessor getters
        // that touch ES private fields don't throw; methods are bound below.
        const value = Reflect.get(target, prop)
        if (typeof value !== 'function') return value
        if (!boundMethods.has(prop)) boundMethods.set(prop, value.bind(target))
        return boundMethods.get(prop)
      },
    })
  }, [router, leadId])
}
